export type JsonPathEntry = {
  path: string;
  valuePreview: string;
};

export type JsonSearchParsed =
  | { kind: 'empty' }
  | { kind: 'free'; needle: string }
  | { kind: 'pair'; key: string; value: string };

/** Walk JSON and collect leaf paths (dot + bracket notation for arrays). */
export function flattenJsonForSearch(data: unknown): JsonPathEntry[] {
  const out: JsonPathEntry[] = [];

  function walk(node: unknown, path: string): void {
    if (node === null || typeof node !== 'object') {
      const preview =
        node === null ? 'null' : typeof node === 'string' ? node : JSON.stringify(node);
      out.push({ path: path || '(root)', valuePreview: preview });
      return;
    }

    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        const segment = path ? `${path}[${index}]` : `[${index}]`;
        walk(item, segment);
      });
      return;
    }

    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      const next = path ? `${path}.${key}` : key;
      walk(value, next);
    }
  }

  walk(data, '');
  return out;
}

export function parseJsonSearchQuery(raw: string): JsonSearchParsed {
  const trimmed = raw.trim();
  if (!trimmed) return { kind: 'empty' };

  // detects searches like "amount:100" and splits them into key ("amount") and value ("100").
  // if it doesn't look like key:value, we fall back to a plain text search below.
  const pairMatch = /^([^\s:]+)\s*:\s*(.+)$/s.exec(trimmed);
  if (pairMatch) {
    const keyPart = pairMatch[1].trim();
    const valuePart = pairMatch[2].trim();
    if (keyPart.length > 0 && valuePart.length > 0) {
      return { kind: 'pair', key: keyPart, value: valuePart };
    }
  }

  return { kind: 'free', needle: trimmed };
}

export function filterJsonPathEntries(
  entries: JsonPathEntry[],
  parsed: JsonSearchParsed,
): JsonPathEntry[] {
  if (parsed.kind === 'empty') return [];

  const lower = (s: string) => s.toLowerCase();

  if (parsed.kind === 'free') {
    const n = lower(parsed.needle);
    return entries.filter((e) => lower(e.path).includes(n) || lower(e.valuePreview).includes(n));
  }

  const keyN = lower(parsed.key);
  const valN = lower(parsed.value);
  return entries.filter(
    (e) => lower(e.path).includes(keyN) && lower(e.valuePreview).includes(valN),
  );
}
